import { useEffect } from 'react';

/**
 * The report's floating tooltip, reproducing the design's behaviour exactly.
 *
 * One fixed node is appended to the body and follows the pointer, reading its content from the
 * `data-tip` attribute of whatever is under the cursor. Doing it this way — rather than a tooltip
 * component per bar — keeps the ~90 hoverable segments on this page declarative: a section only has to
 * emit `data-tip`, and nothing re-renders on mouse move.
 */
const useHealthTooltip = (enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;

    const tip = document.createElement('div');
    tip.style.cssText = 'position:fixed;z-index:9999;pointer-events:none;opacity:0;'
      + 'transition:opacity .12s ease;background:#0F1115;color:#fff;'
      + "font:500 12px/1.5 Inter,system-ui,sans-serif;padding:8px 11px;border-radius:8px;"
      + 'box-shadow:0 10px 30px rgba(15,17,21,.22);white-space:nowrap;max-width:320px';
    document.body.appendChild(tip);

    let currentText = null;

    const onMove = (event) => {
      const target = event.target && event.target.closest ? event.target.closest('[data-tip]') : null;
      if (!target) {
        tip.style.opacity = '0';
        return;
      }

      const text = target.getAttribute('data-tip');
      if (currentText !== text) {
        tip.innerHTML = text;
        currentText = text;
      }
      tip.style.opacity = '1';

      const rect = tip.getBoundingClientRect();
      let x = event.clientX + 16;
      let y = event.clientY - rect.height - 14;
      if (x + rect.width > window.innerWidth - 10) x = window.innerWidth - rect.width - 10;
      if (y < 10) y = event.clientY + 20;
      tip.style.left = `${x}px`;
      tip.style.top = `${y}px`;
    };

    // Capture phase so the listener still fires over children that stop propagation.
    document.addEventListener('mousemove', onMove, true);

    return () => {
      document.removeEventListener('mousemove', onMove, true);
      tip.remove();
    };
  }, [enabled]);
};

export default useHealthTooltip;
